import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
  apiErrorText,
  useMarkPaymentRequestPaidMutation,
  type FinPaymentRequestRow,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { IconX } from '@shared/ui/icons';
import { moneyInput, parseMoney } from '@shared/lib/money';
import { todayIso } from '@shared/lib/localDate';
import { formatMoney } from './financierFormat';

interface Props {
  open: boolean;
  onClose: () => void;
  request: FinPaymentRequestRow | null;
  onSuccess?: () => void;
}

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
  padding: 16px;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;

  &:focus {
    outline: none;
  }
`;

const ModalHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ModalBody = styled.form`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const RequestInfoCard = styled.div`
  padding: 12px 14px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const ErrorAlert = styled.div`
  padding: 8px 12px;
  border-radius: 6px;
  background: #ef444422;
  border: 1px solid #ef4444;
  color: ${({ theme }) => (theme.name === 'dark' ? '#f87171' : '#b91c1c')};
  font-size: 12px;
`;

const SuccessAlert = styled.div`
  padding: 10px 14px;
  border-radius: 6px;
  background: rgba(16, 185, 129, 0.12);
  border: 1px solid #10b981;
  color: ${({ theme }) => (theme.name === 'dark' ? '#69D483' : '#15803d')};
  font-size: 13px;
  font-weight: 600;
`;

const RemainingInfoText = styled.div<{ $active: boolean }>`
  font-weight: 600;
  color: ${({ $active, theme }) => {
    if ($active) {
      return theme.name === 'dark' ? theme.colors.status.success : '#15803d';
    }
    return theme.colors.text.secondary;
  }};
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Input = styled.input`
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: #10b981;
  }
`;

const ModalFooter = styled.div`
  padding: 16px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

export function MarkPaidModal({ open, onClose, request, onSuccess }: Props) {
  const [markPaid, { isLoading }] = useMarkPaymentRequestPaidMutation();
  // Дата по местному времени, а не по UTC: с 00:00 до 03:00 по Москве UTC — ещё вчера
  const todayStr = todayIso();

  const [paidAmount, setPaidAmount] = useState<string>('');
  const [paymentDate, setPaymentDate] = useState(todayStr);
  const [docNumber, setDocNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const modalContentRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  const approvedTotal = request?.approved_amount || request?.amount || 0;
  const currentPaid = request?.paid_amount || 0;
  const rawRemaining = Math.max(0, approvedTotal - currentPaid);
  const remaining = Math.round(rawRemaining * 100) / 100;
  const isFullyPaid =
    remaining <= 0.005 ||
    request?.payment_execution_status === 'paid' ||
    request?.status === 'executed';

  // On open / close / request change: reset form fields cleanly so previous values do not leak into another request.
  // По id заявки, а не по объекту: обновление списка заявок не стирает введённое.
  const requestId = request?.id ?? null;
  useEffect(() => {
    if (request && open) {
      const appr = request.approved_amount || request.amount;
      const alreadyPaid = request.paid_amount || 0;
      const rem = Math.max(0, Math.round((appr - alreadyPaid) * 100) / 100);
      const isPaid = rem <= 0.005 || request.payment_execution_status === 'paid' || request.status === 'executed';

      setPaidAmount(isPaid ? '' : moneyInput(rem));
      setPaymentDate(todayStr);
      setDocNumber('');
      setNotes('');
      setError(null);
    } else if (!open) {
      setPaidAmount('');
      setPaymentDate(todayStr);
      setDocNumber('');
      setNotes('');
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- сброс только при смене заявки или открытии
  }, [requestId, open, todayStr]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        if (!isFullyPaid && firstInputRef.current) {
          firstInputRef.current.focus();
        } else {
          modalContentRef.current?.focus();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [open, isFullyPaid]);

  if (!open || !request) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    if (isFullyPaid) {
      setError('Заявка полностью оплачена, остаток 0 ₽');
      return;
    }
    const amt = parseMoney(paidAmount);
    if (amt === null) {
      setError('Введите сумму оплаты, например 125 000,50');
      return;
    }
    if (amt <= 0) {
      setError('Сумма оплаты должна быть больше нуля');
      return;
    }
    if (amt > remaining + 0.005) {
      setError(`Сумма оплаты (${formatMoney(amt)}) превышает непогашенный остаток (${formatMoney(remaining)})`);
      return;
    }

    try {
      setError(null);
      await markPaid({
        id: request.id,
        paid_amount: amt,
        payment_date: paymentDate || undefined,
        bank_doc_number: docNumber.trim() || undefined,
        notes: notes.trim() || undefined,
      }).unwrap();

      onClose();
      if (onSuccess) onSuccess();
    } catch (err: unknown) {
      setError(apiErrorText(err, 'Не удалось сохранить оплату'));
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent
        ref={modalContentRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mark-paid-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle id="mark-paid-modal-title">Отметка оплаты по банковской выписке</ModalTitle>
          <Button $variant="ghost" $size="sm" onClick={onClose} aria-label="Закрыть">
            <IconX size={14} />
          </Button>
        </ModalHeader>

        <ModalBody onSubmit={handleSubmit}>
          {error && <ErrorAlert>{error}</ErrorAlert>}

          {isFullyPaid && (
            <SuccessAlert>
              Заявка полностью оплачена, остаток 0 ₽
            </SuccessAlert>
          )}

          <RequestInfoCard>
            <div>
              Заявка: <b>{request.request_number}</b> · Контрагент: <b>{request.counterparty_name}</b>
            </div>
            <div>
              Утверждено к оплате: <b>{formatMoney(approvedTotal)}</b> · Ранее оплачено: <b>{formatMoney(currentPaid)}</b>
            </div>
            <RemainingInfoText $active={!isFullyPaid && remaining > 0}>
              Остаток к списанию: {formatMoney(isFullyPaid ? 0 : remaining)}
            </RemainingInfoText>
          </RequestInfoCard>

          <FormGroup>
            <Label htmlFor="mark-paid-amount-input">Сумма оплаты (₽)*</Label>
            <Input
              ref={firstInputRef}
              id="mark-paid-amount-input"
              type="text"
              inputMode="decimal"
              required
              disabled={isFullyPaid}
              value={paidAmount}
              onChange={(e) => setPaidAmount(e.target.value)}
              placeholder={isFullyPaid ? 'Заявка уже полностью оплачена' : 'Сумма списания по банковской выписке'}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="mark-paid-date-input">Дата списания банком</Label>
            <Input
              id="mark-paid-date-input"
              type="date"
              disabled={isFullyPaid}
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="mark-paid-doc-input">Номер и реквизиты платёжного поручения</Label>
            <Input
              id="mark-paid-doc-input"
              disabled={isFullyPaid}
              value={docNumber}
              onChange={(e) => setDocNumber(e.target.value)}
              placeholder="Например: ПП № 1842 от 21.10.2026"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="mark-paid-notes-input">Комментарий казначея</Label>
            <Input
              id="mark-paid-notes-input"
              disabled={isFullyPaid}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Опционально: банк, детали списания…"
            />
          </FormGroup>
        </ModalBody>

        <ModalFooter>
          <Button $variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button $variant="primary" onClick={handleSubmit} disabled={isLoading || isFullyPaid}>
            {isLoading ? 'Сохранение…' : isFullyPaid ? 'Полностью оплачено' : 'Подтвердить оплату'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
}
